import { ContentContext, LocaleContext, RegionContext } from 'Components/hooks/context';
import React, { useContext, useEffect, useState } from 'react';

import AtlasMetadataClient from '@reuters-graphics/graphics-atlas-client';
import GlobalRateMap from '@reuters-graphics/chart-module-globalratemap';
import LoaderPanel from 'Components/common/LoaderPanel';
import classnames from 'classnames';
import { component } from './styles.module.scss';
import debounce from 'lodash/debounce';

const atlas = new AtlasMetadataClient();
const customSettings = {
  europe: {
    map_custom_projections: {
      clip_box: [[-30, 71], [66, 34]],
    },
    annotations: {
      name: ['russia', 'spain'],
      value: ['RU', 'ES'],
    },
    heightRatio: (width, breakpoint) => (width < breakpoint ? 1 : 0.7),
  },
  'northern-america': {
    map_custom_projections: {
      clip_box: [[-150, 90], [-11, 25]],
    },
    annotations: {
      name: ['US', 'CA'],
      value: ['US', 'CA'],
    },
    mobile: false,
    heightRatio: (width, breakpoint) => (width < breakpoint ? 0.6 : 0.4),
  },
  'latin-america-and-the-caribbean': {
    map_custom_projections: {
      clip_box: [[-123, 35], [-32, -55]],
    },
    annotations: {
      name: ['BR', 'MX'],
      value: ['BR', 'MX'],
    },
    mobile: false,
    heightRatio: (width, breakpoint) => (width < breakpoint ? 1 : 0.7),
  },
  oceania: {
    map_custom_projections: {
      center: [0, 5],
      rotate: [-180, 0],
      clip_box: [[111, 2], [230, -50]],
    },
    annotations: {
      name: ['AU', 'NZ', 'french-polynesia', 'papua-new-guinea'],
      value: ['AU', 'NZ', 'french-polynesia', 'papua-new-guinea'],
    },
    mobile: false,
    heightRatio: (width, breakpoint) => (width < breakpoint ? 0.5 : 0.4),
  },
  'asia-and-the-middle-east': {
    annotations: {
      name: ['india', 'indonesia', 'PH'],
      value: ['india', 'indonesia', 'PH'],
    },
    heightRatio: (width, breakpoint) => (width < breakpoint ? 1 : 0.55),
  },
  africa: {
    map_custom_projections: {
      clip_box: [[-35, 40], [67, -35]],
    },
    mobile: false,
    heightRatio: (width, breakpoint) => (width < breakpoint ? 1 : 0.6),
  },
};

const chart = new GlobalRateMap();

const RateMap = ({ globalStatistics }) => {
  const { distributionRanks } = globalStatistics;
  const { cases } = distributionRanks;
  const regionSlug = useContext(RegionContext);
  const locale = useContext(LocaleContext);
  const content = useContext(ContentContext);

  const region = atlas.getRegion(regionSlug);
  const countryList = region.countries.map(c => c.isoAlpha2);

  const [topology, setTopology] = useState(null);

  const data = Object.keys(cases)
    .filter(c => countryList.includes(c))
    .map(country => ({
      key: country,
      value: cases[country],
    }));

  const chartContainer = React.createRef();

  const resize = debounce(() => {
    chart.draw();
  }, 250);

  const fetchTopojson = (region) => {
    fetch(`https://cdn.jsdelivr.net/npm/@reuters-graphics/graphics-atlas-client@latest/topojson/custom/${region}.json`)
      .then(res => res.json())
      .then((topology) => {
        setTopology(topology);
      });
  };

  useEffect(() => {
    setTopology(null);
  }, [regionSlug]);

  useEffect(() => {
    if (topology) return;
    const node = chartContainer.current;
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        fetchTopojson(regionSlug);
        observer.unobserve(node);
      });
    };

    const observerOptions = {
      rootMargin: '1000px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    observer.observe(node);
    return () => {
      observer.unobserve(node);
    };
  }, [regionSlug, chartContainer, topology]);

  // Draw chart
  useEffect(() => {
    if (!topology) return;
    const defaultSettings = {
      locale,
      map_stroke_width: 1,
      map_stroke_color: '#2f353f',
      map_fill: 'rgba(153,153,153,0.25)',
      map_stroke_color_active: 'rgba(153,153,153,0.75)',
      spike_size: 5,
      spike_height: 35,
      spike_stroke_width: 1.6,
      spike_highlight_stroke_width: 3,
      hover_gap: 18,
      heightRatio: (width, breakpoint) => (width < breakpoint ? 0.8 : 0.5),
      map_custom_projections: {
        clip_box: null,
        projection: 'geoNaturalEarth1',
        center: null,
        scale: null,
        rotate: null,
      },
      annotations: {
        name: [],
        value: [],
      },
      // If you want interaction on hover
      interaction: true,
      // If you want the inset on mobile
      mobile: true,
      refBox: {
        // height of the inset box
        height: 90,
        // width of the inset box
        width: 180,
        // width breakpoint at which to show inset
        breakpoint: 900,
        // what is the width of the svg going to be
        useWidth: (width, factor) => (width * factor),
        // the factor that is to be used in the above function
        factor: 2.2,
      },
      key: {
        text: {
          main_text: content.common.mapText.legend.mainText,
          red_peak: content.common.mapText.legend.redPeak,
          orange_peak: content.common.mapText.legend.orangePeak,
          white_peak: content.common.mapText.legend.whitePeak,
        },
      },
      at_peak_text: content.common.peak.atPeak,
      of_peak_text: content.common.mapText.ofPeak,
    };
    chart
      .selection(chartContainer.current)
      .data(data)
      .topojson(topology)
      .props({
        ...defaultSettings, ...customSettings[regionSlug],
      })
      .draw();
  }, [regionSlug, data, locale, chartContainer, topology]);

  // Resize chart
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [resize]);

  return (
    <div className={classnames(component)}>
      <div className='global-rate-map' ref={chartContainer} />
      {!topology && (
        <LoaderPanel height={200} />
      )}
      <div className='section-spacer-internal' />
    </div>
  );
};

export default RateMap;
